import { useQuery } from "@tanstack/react-query";
import { workflowContainerEmptyApi } from "./axios";
import {
  DataI,
  ServiceResponseI,
  PropsWorkflowContainerEmptyI,
  WorkflowContainerEmptyI,
  ObjPostI,
  EnhancedServiceResponseI,
} from "../../interfaces";
import {
  ValidateCurrentToken,
  LogoutSystem,
  generateOfflineId,
  validateServiceWorker,
} from "../../helpers";
import { AxiosError } from "axios";
import { useRouterStore } from "../../stores";

const getWorkflowContainerEmptys = async ({
  pagination,
  sorting,
  globalFilter,
  producer,
  farm,
  client,
  range,
  rangeVal,
}: PropsWorkflowContainerEmptyI): Promise<
  ServiceResponseI<DataI<WorkflowContainerEmptyI>>
> => {
  try {
    const validateToken = ValidateCurrentToken();

    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const params = new URLSearchParams();

    params.append("page", (pagination!.pageIndex + 1).toString());
    params.append("limit", pagination!.pageSize.toString());
    params.append("search", globalFilter ?? "");
    params.append("sorting", JSON.stringify(sorting ?? []));
    client && params.append("client", client.toString());
    producer && params.append("producer", producer.toString());
    farm && params.append("farm", farm.toString());
    rangeVal && params.append("range", JSON.stringify(range));

    const { data } = await workflowContainerEmptyApi.get<
      ServiceResponseI<DataI<WorkflowContainerEmptyI>>
    >("", { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    console.log(err);
    const obj = (err as AxiosError).response;

    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }

    throw new Error((obj?.data as Error).message);
  }
};

export const getAllWorkflowContainerEmptys = async (): Promise<
  ServiceResponseI<WorkflowContainerEmptyI[]>
> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerEmptyApi.get<
      ServiceResponseI<WorkflowContainerEmptyI[]>
    >(`/all-no-pagination`, { headers: { Authorization: validateToken } });

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerEmptyPost = async <T>(
  getForms: T
): Promise<EnhancedServiceResponseI<ObjPostI>> => {
  const { addProcessedContainer, byPassSw } = useRouterStore.getState();

  let result: EnhancedServiceResponseI<ObjPostI>;

  const validateToken = ValidateCurrentToken();
  if (!validateToken) throw new Error("TOKEN INVALIDO");

  const containerNumber = (getForms as any)?.container;

  if (byPassSw) {
    result = await performDirectRequest(getForms, validateToken);
    return result;
  }

  const offlineId = generateOfflineId("empty");
  const url = `${process.env.REACT_APP_BACKEND}api/workflow-container-empty`;

  const swReady = await validateServiceWorker();

  if (!swReady) {
    result = await performDirectRequest(getForms, validateToken);
  } else {
    result = await performServiceWorkerRequest(
      url,
      getForms,
      validateToken,
      offlineId
    );
  }

  if (
    result.data &&
    !result.hasError &&
    containerNumber &&
    result.data.created !== false
  ) {
    addProcessedContainer(containerNumber, "empty");
  }

  return result;
};

async function performServiceWorkerRequest<T>(
  url: string,
  getForms: T,
  validateToken: string,
  offlineId: string
): Promise<EnhancedServiceResponseI<ObjPostI>> {
  return new Promise((resolve, reject) => {
    const messageChannel = new MessageChannel();
    let isResolved = false;

    messageChannel.port1.onmessage = (event) => {
      if (isResolved) return;

      isResolved = true;

      console.log(`📬 Respuesta SW para ${offlineId}:`, event.data);

      if (event.data.error) {
        console.error(`❌ Error SW: ${event.data.error}`);
        reject(new Error(event.data.error));
        return;
      }

      // Verificar que la respuesta incluya confirmación de guardado
      if (event.data.saved !== true) {
        console.warn(`⚠️ SW no confirmó guardado para ${offlineId}`);
        reject(
          new Error("Service Worker no confirmó que los datos se guardaron")
        );
        return;
      }

      if (event.data.directSend) {
        resolve({
          data: {
            id: Date.now(),
            alert: "Proceso Guardado con éxito!",
            variant: navigator.onLine ? "info" : "success",
          },
          res: {} as any,
          req: {} as any,
          isOffline: true,
          swResponse: event.data,
          offlineId,
        });
        return;
      }

      resolve({
        data: {
          id: Date.now(),
          alert: navigator.onLine
            ? "Datos encolados, sincronizando..."
            : "Datos guardados offline",
          variant: navigator.onLine ? "info" : "success",
        },
        res: {} as any,
        req: {} as any,
        isOffline: true,
        swResponse: event.data,
        offlineId,
      });
    };

    // ===== ENVÍO AL SERVICE WORKER =====
    const messageData = {
      type: "ENQUEUE_POST",
      url,
      data: getForms,
      offlineId,
      timestamp: Date.now(), // Para debugging
      headers: {
        "Content-Type": "application/json",
        Authorization: validateToken,
        "X-Offline-ID": offlineId,
        "X-Client-Type": "mobile-pwa", // Identificador para móviles
      },
    };

    try {
      const swController = navigator.serviceWorker.controller;
      if (!swController) {
        reject(
          new Error("Service Worker controller perdido durante la operación")
        );
        return;
      }

      swController.postMessage(messageData, [messageChannel.port2]);
      console.log(`📨 Mensaje enviado al SW para ${offlineId}`);
    } catch (error: any) {
      console.error(`❌ Error enviando al SW:`, error);
      reject(
        new Error(`Error comunicándose con Service Worker: ${error.message}`)
      );
    }
  });
}

async function performDirectRequest<T>(
  getForms: T,
  validateToken: string
): Promise<EnhancedServiceResponseI<ObjPostI>> {
  try {
    const directResponse = await workflowContainerEmptyApi.post<
      ServiceResponseI<ObjPostI>
    >("", getForms, {
      headers: { Authorization: validateToken },
      timeout: 60000, // Reducido para móviles
    });

    return directResponse.data;
  } catch (directError) {
    console.error("❌ Error en petición directa:", directError);
    throw new Error(
      "Service Worker no disponible y petición directa falló. Verifique su conexión."
    );
  }
}

export const generateWfEmptyPdfTarjabyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerEmptyApi.get<ServiceResponseI<any>>(
      `/generate-pdf-tarja/${id}`
      // { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerEmptyPut = async <T>(
  id: number,
  updateForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerEmptyApi.put<
      ServiceResponseI<string>
    >(`/${id}`, updateForms, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerEmptyById = async (
  id: number
): Promise<ServiceResponseI<WorkflowContainerEmptyI>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerEmptyApi.get<
      ServiceResponseI<WorkflowContainerEmptyI>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerEmptyForExternalById = async (
  id: string
): Promise<ServiceResponseI<WorkflowContainerEmptyI>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerEmptyApi.get<
      ServiceResponseI<WorkflowContainerEmptyI>
    >(
      `/external/${id}`
      // { headers: { Authorization: validateToken } }
    );
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerEmptyCount = async (
  type = "hoy"
): Promise<ServiceResponseI<{ total: number }>> => {
  try {
    const params = new URLSearchParams();

    type && params.append("type", type.toString());

    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerEmptyApi.get<
      ServiceResponseI<{ total: number }>
    >(`/count`, { params, headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowContainerEmptyDelete = async (
  id: number
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerEmptyApi.delete<
      ServiceResponseI<string>
    >(`/${id}`, { headers: { Authorization: validateToken } });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const useWorkflowContainerEmptys = ({
  sorting,
  pagination,
  globalFilter,
  producer,
  farm,
  client,
  range,
  rangeVal,
}: PropsWorkflowContainerEmptyI) => {
  const workflowContainerEmptysQuery = useQuery(
    [
      "workflowContainerEmptys",
      {
        pagination,
        sorting,
        globalFilter,
        producer,
        farm,
        client,
        range,
        rangeVal,
      },
    ],
    () =>
      getWorkflowContainerEmptys({
        pagination,
        sorting,
        globalFilter,
        producer,
        farm,
        client,
        range,
        rangeVal,
      }),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
    }
  );

  return {
    workflowContainerEmptysQuery,
  };
};

export const generateWfEmptyPdfReportbyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    // const validateToken = ValidateCurrentToken();
    // if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerEmptyApi.get<ServiceResponseI<any>>(
      `/generate-pdf-report/${id}`
      // { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const generateWfEmptyTarjaTextbyId = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerEmptyApi.get<ServiceResponseI<any>>(
      `/tarja-text/${id}`,
      { headers: { Authorization: validateToken } }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const downloadWfEmptyFilesById = async (
  id: number
): Promise<ServiceResponseI<any>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");

    const { data } = await workflowContainerEmptyApi.get<ServiceResponseI<any>>(
      `/download-full-media/${id}`,
      { headers: { Authorization: validateToken }, responseType: "blob" }
    );

    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      console.error(obj.statusText);
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};

export const workflowEmptySendReportById = async <T>(
  getForms: T
): Promise<ServiceResponseI<string>> => {
  try {
    const validateToken = ValidateCurrentToken();
    if (!validateToken) throw new Error("TOKEN INVALIDO");
    const { data } = await workflowContainerEmptyApi.post<
      ServiceResponseI<string>
    >(`/send-report-by-workflow-id`, getForms, {
      headers: { Authorization: validateToken },
    });
    return data;
  } catch (err) {
    const obj = (err as AxiosError).response;
    if (obj?.status === 401) {
      LogoutSystem();
    }
    throw new Error((obj?.data as Error).message);
  }
};
